/**
 * View-модель блока «История заключений» + мапперы из бэкенд-сущностей.
 *
 * Бэкенд отдаёт строки журнала как { status:boolean, comment, created_at, owner }.
 * UI-компоненты работают с плоской моделью ниже (дата/время раздельно + ФИО строкой).
 */
import type {
  ReviewerReportEntity,
  ReviewerReportOwner,
} from "@/api/reviewer-reports/reviewerReportsApi";

/** UI-статус вердикта */
export type VerdictStatus = "honest" | "dishonest";

/** Строка журнала для презентационных компонентов */
export interface VerdictHistoryEntry {
  id: number;
  date: string; // "21.10.2026"
  time: string; // "11:02"
  author: string; // ФИО проктора
  status: VerdictStatus;
  comment?: string;
  /** id автора — для гейтинга «это моё заключение» (edit/delete) */
  ownerId: number;
  /**
   * true  → автор реально сменил вердикт (или это его первое заключение)
   *         → подпись «Сменил(а) статус на …»
   * false → тот же статус, что и в его прошлом заключении подряд
   *         → подпись «Добавил(а) комментарий»
   * Считается на фронте по последовательности (см. toHistoryEntries).
   */
  isStatusChange: boolean;
}

/** boolean бэка → UI-статус */
export const boolToStatus = (b: boolean): VerdictStatus => (b ? "honest" : "dishonest");

/** UI-статус → 0|1 для POST/PUT */
export const statusToCode = (s: VerdictStatus): 0 | 1 => (s === "honest" ? 1 : 0);

/** ФИО автора из owner-объекта */
export function authorName(owner: ReviewerReportOwner): string {
  return [owner.lastname, owner.firstname, owner.patronymic].filter(Boolean).join(" ").trim();
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Бэкенд-сущность → строку журнала (флаг isStatusChange проставит toHistoryEntries) */
export function toHistoryEntry(e: ReviewerReportEntity): VerdictHistoryEntry {
  const d = new Date(e.created_at);
  const valid = !Number.isNaN(d.getTime());
  return {
    id: e.id,
    date: valid ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : "",
    time: valid ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "",
    author: authorName(e.owner),
    status: boolToStatus(e.status),
    comment: e.comment || undefined,
    ownerId: e.owner.id,
    isStatusChange: true,
  };
}

/**
 * Бэкенд-сущности (новыми сверху) → журнал с расставленным isStatusChange.
 *
 * «Сменил статус» vs «Добавил комментарий» считаем per-author: сравниваем
 * каждое заключение с предыдущим (более старым) заключением ТОГО ЖЕ автора.
 *  • первое заключение автора или другой статус → сменил статус
 *  • тот же статус подряд                       → добавил комментарий
 * Проходим от старых к новым, поэтому массив реверсим (бэк отдаёт desc).
 */
export function toHistoryEntries(entities: ReviewerReportEntity[]): VerdictHistoryEntry[] {
  const entries = entities.map(toHistoryEntry);
  const lastByOwner = new Map<number, VerdictStatus>();
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i];
    const prev = lastByOwner.get(e.ownerId);
    e.isStatusChange = prev === undefined || prev !== e.status;
    lastByOwner.set(e.ownerId, e.status);
  }
  return entries;
}
